import { FilterSchema } from '../../../molecules/FilterBuilder/types';

// Mesma convenção filter[campo][operador] das outras telas — o backend (GET /billing/charges)
// recebe os params CRUS e interpreta.
export { serializeFilters } from '../../settlement/constants/filterSchemas';

/** Cenário do charge — decide o que a tela oferece em cada fatura. */
export const CHARGE_SCENARIO_LABELS: Record<string, string> = {
    RECEIVABLE: 'Recebível',
    INSTALLMENT: 'Parcela',
    GUARANTEE: 'Garantia',
};

/** Status resolvido do charge. PENDING é derivado na view: devendo e sem fatura aberta. */
export const CHARGE_STATUS_LABELS: Record<string, string> = {
    OPEN: 'Em aberto',
    PARTIALLY_PAID: 'Parcial',
    PAID: 'Pago',
    OVERPAID: 'Paga pós Liq.',
    PENDING: 'Falta faturar',
    CANCELLED: 'Cancelado',
    SUPERSEDED: 'Renegociado',
    PAID_WITH_LOSS: 'Pago c/ perda',
};

export const chargeFilterSchema: FilterSchema[] = [
    // ─── visíveis ─────────────────────────────────────────────────────────────
    { key: 'contract_number', label: 'Nº Contrato', type: 'text', operators: ['contains', 'eq'], defaultOperator: 'contains', visible: true },
    {
        key: 'charge_status', label: 'Status', type: 'enum', operators: ['in', 'not_in'], defaultOperator: 'in', visible: true, options: [
            { value: 'OPEN', label: 'Em aberto' },
            { value: 'PARTIALLY_PAID', label: 'Parcial' },
            { value: 'PAID', label: 'Pago' },
            { value: 'OVERPAID', label: 'Paga pós Liq.' },
            { value: 'PENDING', label: 'Falta faturar' },
            { value: 'CANCELLED', label: 'Cancelado' },
            { value: 'SUPERSEDED', label: 'Renegociado' },
        ]
    },
    {
        key: 'charge_scenario', label: 'Natureza', type: 'enum', operators: ['in'], defaultOperator: 'in', visible: true, options: [
            { value: 'RECEIVABLE', label: 'Recebível' },
            { value: 'INSTALLMENT', label: 'Parcela' },
            { value: 'GUARANTEE', label: 'Garantia' },
        ]
    },
    { key: 'due_date', label: 'Vencimento', type: 'date', operators: ['before', 'after', 'between_dates'], defaultOperator: 'between_dates', visible: true },

    // ─── ocultos (via "+ Filtro") ─────────────────────────────────────────────
    { key: 'is_overdue', label: 'Vencido', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },
    { key: 'collecting', label: 'Em cobrança', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },
    { key: 'has_overpayment', label: 'Tem Pgto pós Liq.', type: 'boolean', operators: ['is_true'], defaultOperator: 'is_true', visible: false },
    // Estados do TÍTULO (AF-3674): a cobrança saiu das nossas mãos. Ocultos porque são busca de
    // exceção — quem procura sabe o que procura.
    { key: 'subrogation', label: 'Sub-rogado', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },
    { key: 'judicial_petition', label: 'Jurídico', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },
    { key: 'amount', label: 'Valor', type: 'number', operators: ['gte', 'lte', 'between'], defaultOperator: 'between', visible: false },
    { key: 'paid_amount', label: 'Valor pago', type: 'number', operators: ['gte', 'lte'], defaultOperator: 'gte', visible: false },
    { key: 'remaining_amount', label: 'Falta pagar', type: 'number', operators: ['gte', 'lte'], defaultOperator: 'gte', visible: false },
    { key: 'open_invoices_count', label: 'Faturas abertas', type: 'number', operators: ['eq', 'gte', 'lte'], defaultOperator: 'eq', visible: false },
    { key: 'invoices_count', label: 'Total de faturas', type: 'number', operators: ['eq', 'gte'], defaultOperator: 'gte', visible: false },
    { key: 'sequencial_number', label: 'Sequencial', type: 'text', operators: ['eq', 'contains'], defaultOperator: 'eq', visible: false },
    { key: 'document_title', label: 'Título/NF', type: 'text', operators: ['contains', 'eq'], defaultOperator: 'contains', visible: false },
    // PAGADOR do título (settlement_v2.title_debt.payer) — quem deve, não quem emitiu. O CEDENTE
    // é outro campo, `assignor_name`, e vive na fatura: em cessão são empresas diferentes, e
    // chamar este de cedente fazia a tela afirmar o participante errado.
    { key: 'title_payer_name', label: 'Pagador Nome', type: 'text', operators: ['contains'], defaultOperator: 'contains', visible: false },
    // Documento aceita `eq` além de `contains`: é o operador que o atalho da lista de pagamentos
    // usa, e documento é chave — busca parcial ali traria outro CNPJ que só compartilha dígitos.
    { key: 'title_payer_document', label: 'Pagador Doc.', type: 'text', operators: ['contains', 'eq'], defaultOperator: 'contains', visible: false },
    {
        key: 'operation_type', label: 'Tipo de operação', type: 'enum', operators: ['in'], defaultOperator: 'in', visible: false, options: [
            { value: 'LOAN', label: 'Empréstimo' },
            { value: 'RECEIVABLES_ASSIGNMENT', label: 'Cessão de recebíveis' },
            { value: 'ACKNOWLEDGEMENT_OF_DEBT', label: 'Confissão de dívida' },
            { value: 'STRUCTURED_OPERATION', label: 'Operação estruturada' },
        ]
    },
    { key: 'operation_active', label: 'Operação ativa', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },
    { key: 'credit_operation_id', label: 'Operação (id)', type: 'text', operators: ['eq'], defaultOperator: 'eq', visible: false },
    { key: 'charge_id', label: 'Charge (id)', type: 'text', operators: ['eq'], defaultOperator: 'eq', visible: false },
];
